import type { Theme } from '@mui/material'
import { Box } from '@mui/material'
import { styled } from '@mui/material/styles'
import { Outlet } from 'react-router'
import useCurrentGameRoute from '../../../hooks/useCurrentGameRoute'
import useLayout from '../../../hooks/useLayout'
import Navbar from './Navbar'
import Sidebar from './Sidebar'

const LayoutRoot = styled('div')(({ theme }) => ({
  backgroundColor: theme.palette.background.default,
  display: 'flex',
  height: '100%',
  overflow: 'hidden',
  width: '100%',
}))

const LayoutWrapper = styled(Box, {
  shouldForwardProp: (propName: PropertyKey) =>
    propName !== 'hasChrome' && propName !== 'isSidebarOpen',
})<{ hasChrome: boolean; isSidebarOpen: boolean }>(
  ({
    theme,
    hasChrome,
    isSidebarOpen,
  }: {
    theme: Theme
    hasChrome: boolean
    isSidebarOpen: boolean
  }) => ({
    display: 'flex',
    flex: '1 1 auto',
    overflow: 'hidden',
    paddingTop: hasChrome ? '64px' : 0,
    transition: theme.transitions.create('padding', {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.shortest,
    }),
    [theme.breakpoints.up('lg')]: {
      paddingLeft: hasChrome ? (isSidebarOpen ? '280px' : '80px') : 0,
    },
  })
)

const LayoutContainer = styled('div')({
  display: 'flex',
  flex: '1 1 auto',
  overflow: 'hidden',
})

const LayoutContent = styled('div')({
  flex: '1 1 auto',
  height: '100%',
  overflow: 'auto',
  position: 'relative',
  WebkitOverflowScrolling: 'touch',
})

export default function Layout() {
  const { isSidebarOpen } = useLayout()
  const { shouldHideGameChrome } = useCurrentGameRoute()
  const shouldShowChrome = !shouldHideGameChrome

  return (
    <LayoutRoot>
      {shouldShowChrome ? <Navbar /> : null}
      {shouldShowChrome ? <Sidebar /> : null}
      <LayoutWrapper
        hasChrome={shouldShowChrome}
        isSidebarOpen={shouldShowChrome && isSidebarOpen}
      >
        <LayoutContainer>
          <LayoutContent>
            <Outlet />
          </LayoutContent>
        </LayoutContainer>
      </LayoutWrapper>
    </LayoutRoot>
  )
}
